import { useMemo } from 'react';
import { useAgentContext } from '@Providers';
import { useQuery } from '@tanstack/react-query';

import { GuidedJourney, GuidedJourneyRaw } from '@Models';
import { bopsApi } from '@Network';

export const useGuidedJourneys = () => {
  const { selectedAgent } = useAgentContext();
  const sourceName = selectedAgent?.source_name;

  const {
    data: rawJourneys,
    isLoading: isQueryLoading,
    ...queryState
  } = useQuery<GuidedJourneyRaw[]>({
    queryKey: ['guided-journeys', sourceName],
    queryFn: () => bopsApi.getGuidedJourneys(sourceName),
    enabled: !!sourceName,
    staleTime: 5 * 60 * 1000, // 5 minutes - journeys don't change often
  });

  // Transform raw journeys to selected language
  const journeys = useMemo<GuidedJourney[] | undefined>(() => {
    if (!rawJourneys) return undefined;

    // Get agent language, default to 'en'
    const agentLanguage = (selectedAgent?.language || 'en').toLowerCase();

    return rawJourneys.map((rawJourney) => {
      // Get display translation for agent's language, fallback to 'en'
      const translation = rawJourney.translations[agentLanguage] || rawJourney.translations.en;

      return {
        id: rawJourney.id,
        display: translation?.display || '',
        prompt: rawJourney.prompt, // Prompt is always in English (from root level)
        cache: rawJourney.cache,
        title: rawJourney.title,
      };
    });
  }, [rawJourneys, selectedAgent?.language]);

  // Hold the skeleton until an agent is selected AND the query has settled.
  // Prevents a flash of cached/default cards during agent resolution.
  const isLoading = !sourceName || isQueryLoading;

  return {
    ...queryState,
    isLoading,
    data: journeys,
  };
};

export default useGuidedJourneys;
